import { Suspense, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, ChevronDown } from 'lucide-react';
import { Button } from '@reflex/ui/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@reflex/ui/components/ui/dropdown-menu';
import { cn } from '@reflex/ui/lib/utils';
import type { Agent } from '@/lib/types';
import type { AgentAction, AgentDialogAction, AgentSubmenuAction } from './useAgentActions';

const BUTTON_BASE = 'h-7 gap-1.5 text-2xs font-medium';

function buttonClass(action: AgentAction): string {
  return cn(BUTTON_BASE, action.destructive && 'border-destructive/30 text-destructive');
}

function DialogActionButton({ action, agent }: { action: AgentDialogAction; agent: Agent }) {
  const [open, setOpen] = useState(false);
  const Dialog = action.Dialog;
  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className={buttonClass(action)}
        onClick={() => setOpen(true)}
        data-replay-target={action.replayTarget}
        data-testid={`agent-action-${action.key}`}
      >
        <action.icon className="h-3 w-3" />
        {action.label}
      </Button>
      {open && (
        <Suspense>
          <Dialog agent={agent} open={open} onOpenChange={setOpen} />
        </Suspense>
      )}
    </>
  );
}

/**
 * A grouped action rendered as one dropdown button, so a family of related
 * choices (the triage marks) costs the panel a single slot instead of one
 * button per choice.
 */
function SubmenuActionButton({ action }: { action: AgentSubmenuAction }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={buttonClass(action)}
          data-testid={`agent-action-${action.key}`}
        >
          <action.icon className={cn('h-3 w-3', action.iconClassName)} />
          {action.label}
          <ChevronDown className="h-3 w-3 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {action.items.map((item) => (
          <DropdownMenuItem key={item.key} onSelect={item.onSelect}>
            <item.icon className={cn('mr-2 h-4 w-4', item.iconClassName)} />
            {item.label}
            {item.checked && <Check className="ml-auto h-4 w-4" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Renders shared {@link AgentAction}s as a row of buttons for the agent
 * detail panel — the same set the stream header exposes in its three-dot
 * menu, presented inline so the panel doubles as the agent's action
 * surface. Responsive header-only hints are ignored here.
 */
export function AgentActionButtons({ actions, agent }: { actions: AgentAction[]; agent: Agent }) {
  if (actions.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="agent-action-buttons">
      {actions.map((action) =>
        action.kind === 'submenu' ? (
          <SubmenuActionButton key={action.key} action={action} />
        ) : action.kind === 'dialog' ? (
          <DialogActionButton key={action.key} action={action} agent={agent} />
        ) : action.kind === 'link' ? (
          <Button
            key={action.key}
            variant="outline"
            size="sm"
            className={buttonClass(action)}
            asChild
            data-testid={`agent-action-${action.key}`}
          >
            <Link to={action.to}>
              <action.icon className="h-3 w-3" />
              {action.label}
            </Link>
          </Button>
        ) : (
          <Button
            key={action.key}
            variant="outline"
            size="sm"
            className={buttonClass(action)}
            onClick={action.onSelect}
            disabled={action.pending}
            data-replay-target={action.replayTarget}
            data-testid={`agent-action-${action.key}`}
          >
            <action.icon className="h-3 w-3" />
            {action.label}
          </Button>
        ),
      )}
    </div>
  );
}
